/**
 * Luxury Bed Product Customizer
 * Handles height range slider, fabric selection, color swatches, and price calculations
 * for luxury bed products
 */

class LuxuryBedCustomizer {
    constructor() {
        this.fabricData = {};
        this.currentFabric = 'Plush';
        this.currentColor = '';
        this.basePrice = 0;
        this.heightAddonPrice = 0;
        this.mattressAddonPrice = 0;
        this.gasLiftAddonPrice = 0;
        this.currencyFormat = '£{{amount}}';

        // Height price tiers (in pence) — tierStart is used for variant matching
        this.heightTiers = [
            { min: 60, max: 64, price: 0, label: 'Standard', tierStart: null },
            { min: 65, max: 69, price: 5000, label: '65"–70"', tierStart: 65 },
            { min: 70, max: 74, price: 10000, label: '70"–75"', tierStart: 70 },
            { min: 75, max: 79, price: 15000, label: '75"–80"', tierStart: 75 },
            { min: 80, max: 84, price: 20000, label: '80"–85"', tierStart: 80 },
            { min: 85, max: 89, price: 25000, label: '85"–90"', tierStart: 85 },
            { min: 90, max: 94, price: 30000, label: '90"–95"', tierStart: 90 },
            { min: 95, max: 100, price: 35000, label: '95"–100"', tierStart: 95 }
        ];

        this.heightVariants = []; // will hold variants from luxury-headboard-height

        this.init();
    }

    init() {
        console.log('LuxuryBed Customizer initializing...');

        // Load fabric data from JSON
        this.loadFabricData();

        // Fetch height product variants
        this.fetchHeightVariants();

        // Get base price from selected variant
        this.updateBasePrice();

        // Set up event listeners
        this.setupEventListeners();

        // Load initial color swatches for default fabric
        this.loadColorSwatches(this.currentFabric);

        // Initialize slider fill
        this.updateSliderFill();

        // Prime Klarna placement amount on initial load (no addons yet, so total == basePrice)
        this.updatePriceDisplay();

        console.log('LuxuryBed Customizer initialized successfully!');
    }

    loadFabricData() {
        const fabricDataElement = document.getElementById('headboard-fabrics-data');
        if (fabricDataElement) {
            try {
                this.fabricData = JSON.parse(fabricDataElement.textContent);
            } catch (e) {
                console.error('Error parsing fabric data:', e);
            }
        }
    }

    fetchHeightVariants() {
        fetch('/products/luxury-headboard-height.js')
            .then(res => res.ok ? res.json() : null)
            .then(product => {
                if (product && product.variants) {
                    this.heightVariants = product.variants;
                    console.log('Loaded luxury height variants:', this.heightVariants.length);
                    // Log each variant for debugging
                    this.heightVariants.forEach(v => {
                        console.log('  Variant:', v.id, '|', v.title, '| option1:', v.option1, '| price:', v.price);
                    });
                }
            })
            .catch(err => {
                console.warn('Could not fetch luxury-headboard-height product:', err);
            });
    }

    setupEventListeners() {
        // Height slider
        const slider = document.getElementById('luxury-height-slider');
        if (slider) {
            slider.addEventListener('input', (e) => this.handleHeightSlider(e));
        }

        // Fabric selector change
        const fabricInputs = document.querySelectorAll('input[name="fabric-select"]');
        fabricInputs.forEach(input => {
            input.addEventListener('change', (e) => this.handleFabricChange(e));
        });

        // Variant change (size variant)
        const variantSelectors = document.querySelectorAll('.js-single-option-selector');
        variantSelectors.forEach(selector => {
            selector.addEventListener('change', () => this.handleVariantChange());
        });

        // Listen to the main variant select dropdown
        const section = document.querySelector('[data-section-type="product-template"]');
        const sectionId = section ? section.dataset.sectionId : null;
        if (sectionId) {
            const mainVariantSelect = document.querySelector('.js-product-select--' + sectionId);
            if (mainVariantSelect) {
                mainVariantSelect.addEventListener('change', () => this.handleVariantChange());
            }
        }

        // Listen to existing variant change events
        document.addEventListener('variantChange', () => {
            setTimeout(() => this.handleVariantChange(), 50);
        });

        // Watch for price element changes in the DOM
        this.observePriceChanges();

        // Listen for mattress addon updates
        document.addEventListener('mattress-addon-updated', (e) => {
            console.log('Mattress addon selected:', e.detail);
            this.handleMattressAddonUpdate(e.detail);
        });

        // Listen for gas lift addon updates
        document.addEventListener('gas-lift-addon-updated', (e) => {
            console.log('Gas lift addon selected:', e.detail);
            this.handleGasLiftAddonUpdate(e.detail);
        });
    }

    /**
     * Find a variant whose title starts with the given number.
     * Extracts the first number from each variant title and compares to tierStart.
     * E.g. tierStart=65 will match "65" – 70"", "65"-70"", "65" - 70"" etc.
     */
    findVariantByStartNumber(tierStart) {
        if (!this.heightVariants || !this.heightVariants.length) return null;

        return this.heightVariants.find(v => {
            // Try title first, then option1
            const text = v.title || v.option1 || '';
            // Extract the first number from the string
            const match = text.match(/(\d+)/);
            if (match) {
                return parseInt(match[1]) === tierStart;
            }
            return false;
        });
    }

    handleHeightSlider(event) {
        const height = parseInt(event.target.value);
        const heightLabel = document.getElementById('selected-luxury-height');
        const priceBadge = document.getElementById('luxury-height-price-badge');
        const heightProperty = document.getElementById('luxury-height-property');
        const heightAddonId = document.getElementById('luxury-height-addon-id');
        const heightAddonPrice = document.getElementById('luxury-height-addon-price');

        // Update display
        if (heightLabel) heightLabel.textContent = height + '"';

        // Find matching tier
        const tier = this.heightTiers.find(t => height >= t.min && height <= t.max);

        if (tier) {
            this.heightAddonPrice = tier.price;

            if (tier.price === 0) {
                if (priceBadge) {
                    priceBadge.textContent = '(Standard - £0)';
                    priceBadge.classList.remove('has-price');
                }
                if (heightProperty) heightProperty.value = height + '" (Standard)';
                if (heightAddonId) heightAddonId.value = '';
                if (heightAddonPrice) heightAddonPrice.value = '0';
            } else {
                const priceFormatted = '£' + (tier.price / 100).toFixed(0);
                if (priceBadge) {
                    priceBadge.textContent = '(+' + priceFormatted + ')';
                    priceBadge.classList.add('has-price');
                }
                if (heightProperty) heightProperty.value = height + '" (+' + priceFormatted + ')';
                if (heightAddonPrice) heightAddonPrice.value = tier.price;

                // Find matching variant using number-based matching
                if (this.heightVariants.length > 0 && tier.tierStart) {
                    const matchVariant = this.findVariantByStartNumber(tier.tierStart);
                    if (matchVariant) {
                        if (heightAddonId) heightAddonId.value = matchVariant.id;
                        // Use actual variant price
                        this.heightAddonPrice = matchVariant.price;
                        if (heightAddonPrice) heightAddonPrice.value = matchVariant.price;
                        const actualFormatted = '£' + (matchVariant.price / 100).toFixed(0);
                        if (priceBadge) {
                            priceBadge.textContent = '(+' + actualFormatted + ')';
                        }
                        if (heightProperty) heightProperty.value = height + '" (+' + actualFormatted + ')';
                        console.log('Matched height variant:', matchVariant.id, matchVariant.title, 'price:', matchVariant.price);
                    } else {
                        console.warn('No variant found for tier starting at', tier.tierStart, '- variants loaded:', this.heightVariants.length);
                    }
                } else if (tier.tierStart) {
                    console.warn('Height variants not yet loaded. Variants count:', this.heightVariants.length);
                }
            }
        }

        // Update slider fill
        this.updateSliderFill();

        // Update price display
        this.updatePriceDisplay();
    }

    updateSliderFill() {
        const slider = document.getElementById('luxury-height-slider');
        if (!slider) return;
        const min = parseInt(slider.min);
        const max = parseInt(slider.max);
        const val = parseInt(slider.value);
        const percent = ((val - min) / (max - min)) * 100;
        slider.classList.add('active-fill');
        slider.style.setProperty('--fill-percent', percent + '%');
    }

    handleFabricChange(event) {
        this.currentFabric = event.target.value;

        // Update fabric label
        const fabricLabel = document.getElementById('selected-fabric');
        if (fabricLabel) {
            fabricLabel.textContent = this.currentFabric;
        }

        // Update hidden field
        const fabricProperty = document.getElementById('fabric-property');
        if (fabricProperty) {
            fabricProperty.value = this.currentFabric;
        }

        // Reset color selection
        this.currentColor = '';
        const colorProperty = document.getElementById('color-property');
        if (colorProperty) {
            colorProperty.value = '';
        }

        const colorLabel = document.getElementById('selected-color');
        if (colorLabel) {
            colorLabel.textContent = 'Select a color';
        }

        // Load new color swatches
        this.loadColorSwatches(this.currentFabric);
    }

    loadColorSwatches(fabric) {
        const grid = document.getElementById('color-swatch-grid');
        if (!grid) return;

        // Clear existing swatches
        grid.innerHTML = '';

        // Get colors for this fabric
        const fabricColors = this.fabricData[fabric];
        if (!fabricColors || !fabricColors.colors) {
            console.warn('No colors found for fabric:', fabric);
            return;
        }

        // Create swatch for each color
        fabricColors.colors.forEach((color, index) => {
            const swatchDiv = document.createElement('div');
            swatchDiv.className = 'color-swatch';
            swatchDiv.dataset.colorName = color.name;

            const input = document.createElement('input');
            input.type = 'radio';
            input.name = 'color-select';
            input.value = color.name;
            input.id = `color-${fabric}-${index}`;
            input.addEventListener('change', (e) => this.handleColorChange(e));

            const label = document.createElement('label');
            label.htmlFor = input.id;
            label.title = color.name;

            const img = document.createElement('img');
            img.src = color.swatch;
            img.alt = color.name;
            img.loading = 'lazy';

            label.appendChild(img);
            swatchDiv.appendChild(input);
            swatchDiv.appendChild(label);
            grid.appendChild(swatchDiv);
        });
    }

    handleColorChange(event) {
        this.currentColor = event.target.value;

        // Update color label
        const colorLabel = document.getElementById('selected-color');
        if (colorLabel) {
            colorLabel.textContent = this.currentColor;
        }

        // Update hidden field
        const colorProperty = document.getElementById('color-property');
        if (colorProperty) {
            colorProperty.value = this.currentColor;
        }
    }

    handleVariantChange() {
        console.log('Variant changed, updating prices...');
        this.updateBasePrice();
        this.updatePriceDisplay();
    }

    updateBasePrice() {
        // Method 1: Try to get from product JSON and selected variant ID
        const section = document.querySelector('[data-section-type="product-template"]');
        const sectionId = section ? section.dataset.sectionId : null;

        if (sectionId) {
            const variantSelect = document.querySelector('.js-product-select--' + sectionId);
            if (variantSelect && variantSelect.value) {
                const selectedVariantId = parseInt(variantSelect.value);

                const productJsonElement = document.getElementById('ProductJson-' + sectionId);
                if (productJsonElement) {
                    try {
                        const productData = JSON.parse(productJsonElement.textContent);
                        const selectedVariant = productData.variants.find(v => v.id === selectedVariantId);
                        if (selectedVariant && selectedVariant.price) {
                            this.basePrice = selectedVariant.price;
                            return;
                        }
                    } catch (e) {
                        console.warn('Error parsing product JSON:', e);
                    }
                }

                // Fallback: Extract price from option text
                const selectedOption = variantSelect.options[variantSelect.selectedIndex];
                if (selectedOption) {
                    const priceText = selectedOption.textContent.split('-').pop().trim();
                    this.basePrice = this.parsePriceToInt(priceText);
                    return;
                }
            }
        }

        // Method 2: Try to get from price display element
        const priceElement = document.querySelector('.product-single__price, .product-price__price, [data-product-price]');
        if (priceElement) {
            const priceText = priceElement.textContent || priceElement.innerText;
            this.basePrice = this.parsePriceToInt(priceText);
            return;
        }
    }

    observePriceChanges() {
        const priceElement = document.querySelector('.product-single__price, .product-price__price, [data-product-price]');
        if (!priceElement) return;

        const observer = new MutationObserver(() => {
            // Skip mutations caused by our own syncMainPriceBlock writes
            if (this._syncingMainPrice) return;
            this.updateBasePrice();
            this.updatePriceDisplay();
        });

        observer.observe(priceElement, {
            childList: true,
            characterData: true,
            subtree: true
        });
    }

    parsePriceToInt(priceString) {
        const cleaned = priceString.replace(/[^0-9.]/g, '');
        const float = parseFloat(cleaned);
        return Math.round(float * 100);
    }

    formatMoney(amountInPence) {
        const amount = (amountInPence / 100).toFixed(2);
        return this.currencyFormat.replace('{{amount}}', amount);
    }

    updatePriceDisplay() {
        const baseDisplay = document.getElementById('base-price-display');
        const addonDisplay = document.getElementById('addon-price-display');
        const addonSection = document.getElementById('addon-price-section');
        const totalDisplay = document.getElementById('total-price-display');

        if (baseDisplay) {
            baseDisplay.textContent = this.formatMoney(this.basePrice);
        }

        // Calculate total addon price (height + mattress + gas lift)
        const totalAddonPrice = this.heightAddonPrice + this.mattressAddonPrice + this.gasLiftAddonPrice;

        if (totalAddonPrice > 0) {
            if (addonDisplay) {
                const parts = [];
                if (this.heightAddonPrice > 0) parts.push('Height: +' + this.formatMoney(this.heightAddonPrice));
                if (this.mattressAddonPrice > 0) parts.push('Mattress: +' + this.formatMoney(this.mattressAddonPrice));
                if (this.gasLiftAddonPrice > 0) parts.push('Gas Lift: +' + this.formatMoney(this.gasLiftAddonPrice));

                if (parts.length > 1) {
                    addonDisplay.textContent = '+' + this.formatMoney(totalAddonPrice) + ' (' + parts.join(', ') + ')';
                } else {
                    addonDisplay.textContent = '+' + this.formatMoney(totalAddonPrice) + ' (' + parts[0] + ')';
                }
            }
            if (addonSection) {
                addonSection.style.display = 'flex';
            }
        } else {
            if (addonSection) {
                addonSection.style.display = 'none';
            }
        }

        const total = this.basePrice + totalAddonPrice;

        if (totalDisplay) {
            totalDisplay.textContent = this.formatMoney(total);
        }

        // Sync the main product price block so Klarna OSM (which scrapes
        // .js-product-price / its `content` attribute) sees the addon-inclusive total.
        this.syncMainPriceBlock(total);

        // Update Klarna On-Site Messaging with the new total price
        this.refreshKlarnaMessaging(total);
    }

    syncMainPriceBlock(totalInPence) {
        var mainPriceEls = document.querySelectorAll('.js-product-price');
        if (!mainPriceEls.length) return;
        var formatted = this.formatMoney(totalInPence);
        var contentValue = (totalInPence / 100).toFixed(2);
        this._syncingMainPrice = true;
        mainPriceEls.forEach(function (el) {
            el.textContent = formatted;
            el.setAttribute('content', contentValue);
        });
        var self = this;
        setTimeout(function () { self._syncingMainPrice = false; }, 0);
    }

    refreshKlarnaMessaging(totalInPence) {
        try {
            var amountStr = totalInPence.toString();
            var placements = document.querySelectorAll('klarna-placement');

            // Clone-and-replace each placement so Klarna's custom-element script
            // re-mounts it and reads the fresh data-purchase-amount. Just calling
            // setAttribute + 'refresh-placements' is not enough — the Shopify
            // Klarna OSM app caches the rendered content from the first mount.
            placements.forEach(function (placement) {
                placement.setAttribute('data-purchase-amount', amountStr);
                var fresh = placement.cloneNode(true);
                fresh.setAttribute('data-purchase-amount', amountStr);
                if (placement.parentNode) {
                    placement.parentNode.replaceChild(fresh, placement);
                }
            });

            // Belt-and-braces: tell Klarna to refresh placements
            window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
            window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });

            // KOSMApp API (Shopify Klarna OSM app), if exposed
            if (window.KOSMApp && typeof window.KOSMApp.updatePurchaseAmount === 'function') {
                window.KOSMApp.updatePurchaseAmount(totalInPence);
            }
        } catch (e) {
            console.warn('Klarna messaging update failed:', e);
        }
    }

    handleMattressAddonUpdate(detail) {
        this.mattressAddonPrice = parseInt(detail.price) || 0;
        this.updatePriceDisplay();
    }

    handleGasLiftAddonUpdate(detail) {
        this.gasLiftAddonPrice = parseInt(detail.price) || 0;
        this.updatePriceDisplay();
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        if (document.getElementById('headboard-product-form') && document.getElementById('luxury-height-slider')) {
            window.luxuryBedCustomizer = new LuxuryBedCustomizer();
        }
    });
} else {
    if (document.getElementById('headboard-product-form') && document.getElementById('luxury-height-slider')) {
        window.luxuryBedCustomizer = new LuxuryBedCustomizer();
    }
}
